import { CSSProperties, useState } from 'react';

// Components
import ModalSymphony from 'elements/ModalSymphony';
import InputSymphony from 'elements/InputSymphony';
import CustomAvatar from 'components/ProfileDropdown/CustomAvatar';
import BrandTier from 'elements/BrandTier';
import Loader from 'elements/Loader';

// Hooks
import useBrand from 'Hooks/useBrand';
import useOrganizationV2 from 'Hooks/useOrganizationV2';
import useScreen from 'Hooks/useScreen';
import { useBrandSelectorModal } from 'Hooks/useGlobalModals';

// Utils & Constants
import { styx } from 'modules/Utils';
import Colors from 'modules/Colors';
import Sty from 'modules/Sty';
import { CurrentBrand } from 'modules/Types';

interface BrandSelectorModalProps {
    /** Additional CSS class name */
    className?: string;
    /** Custom style overrides */
    style?: CSSProperties;
}

const BrandSelectorModal = ({
    className,
    style
}: BrandSelectorModalProps) => {
    const { mStyle } = useScreen();
    const {
        loadedBrandsDict,
        getBrand
    } = useBrand();
    const {
        organizationBrandIds
    } = useOrganizationV2();
    const {
        isBrandSelectorOpen,
        closeBrandSelector,
        brandSelectorConfig,
        handleBrandSelection
    } = useBrandSelectorModal();

    const [searchText, setSearchText] = useState('');
    const [isLoadingBrand, setIsLoadingBrand] = useState(false);

    if (!isBrandSelectorOpen || !brandSelectorConfig) {
        return null;
    }

    const {
        title = 'Select a Profile',
        subtitle = 'Choose the profile you want to create content for.',
        brandFilter
    } = brandSelectorConfig;

    // Get available brands
    const availableBrands = organizationBrandIds
        ?.map(brandId => loadedBrandsDict?.[brandId])
        .filter((brand): brand is CurrentBrand => Boolean(brand))
        .filter(brand => {
            // Apply search filter
            const matchesSearch = brand.name?.toLowerCase().includes(searchText.toLowerCase());
            
            // Apply custom brand filter if provided
            const matchesCustomFilter = brandFilter ? brandFilter(brand) : true;
            
            return matchesSearch && matchesCustomFilter;
        }) || [];

    const handleBrandClick = async (brandId: number) => {
        setIsLoadingBrand(true);
        
        try {
            const brand = await getBrand(brandId);
            if (brand) {
                handleBrandSelection(brand);
            }
        } catch (error) {
            console.error('Error selecting brand:', error);
        } finally {
            setIsLoadingBrand(false);
        }
    };

    const renderBrandItem = (brand: CurrentBrand, index: number) => {
        const {
            id,
            name,
            image,
            tier
        } = brand;

        return (
            <div
                key={`brand-selector-${id}-${index}`}
                className="brandSelectorItem"
                style={mStyle(sty, 'brandSelectorItem')}
                onClick={() => handleBrandClick(id)}
            >
                <CustomAvatar
                    className="brandSelectorAvatar"
                    image={image}
                    brandOverride={brand}
                    size={40}
                />
                <div className="brandSelectorInfo" style={sty.brandSelectorInfo}>
                    <div className="brandSelectorName" style={sty.brandSelectorName}>
                        {name}
                    </div>
                    <div className="brandSelectorSlug" style={sty.brandSelectorSlug}>
                        @{brand.slug}
                    </div>
                </div>
                <BrandTier
                    className="brandSelectorTierTag"
                    style={sty.brandSelectorTierTag}
                    tier={tier}
                />
            </div>
        );
    };

    const renderContent = () => {
        if (isLoadingBrand) {
            return (
                <div style={sty.loadingContainer}>
                    <Loader />
                    <p style={sty.loadingText}>Selecting brand...</p>
                </div>
            );
        }

        if (availableBrands.length === 0) {
            return (
                <div style={sty.emptyState}>
                    <p style={sty.emptyStateText}>
                        {searchText 
                            ? `No brands found matching "${searchText}"` 
                            : 'No brands available'
                        }
                    </p>
                </div>
            );
        }

        return (
            <div
                className="brandSelectorScrollView"
                style={styx(
                    mStyle(sty, 'brandSelectorScrollView'),
                    { boxShadow: Sty.boxShadowInset }
                )}
            >
                {availableBrands.map((brand, index) => renderBrandItem(brand, index))}
            </div>
        );
    };

    return (
        <ModalSymphony
            className={`brandSelectorModal ${className || ''}`}
            isOpen={isBrandSelectorOpen}
            onClose={closeBrandSelector}
            title={title}
            subtitle={subtitle}
            maxWidth={450}
            style={style}
        >
            <div className="brandSelectorContent" style={sty.brandSelectorContent}>
                <InputSymphony
                    className="brandSelectorSearchInput"
                    placeholder="Search brands..."
                    value={searchText}
                    onChange={setSearchText}
                    isSearch
                    style={sty.searchInput}
                />
                
                {renderContent()}
            </div>
        </ModalSymphony>
    );
};

const sty: Record<string, CSSProperties> = {
    brandSelectorContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16
    },
    searchInput: {
        marginBottom: 8
    },
    brandSelectorScrollView: {
        maxHeight: 400,
        overflowY: 'auto',
        border: `1px solid ${Colors.greyLight}`,
        borderRadius: 8,
        backgroundColor: Colors.white
    },
    brandSelectorItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: 16,
        cursor: 'pointer',
        borderBottom: `1px solid ${Colors.greyLighter}`,
        transition: 'background-color 0.2s ease'
    },
    brandSelectorItem_hover: {
        backgroundColor: Colors.greyLighter
    },
    brandSelectorInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        flex: 1
    },
    brandSelectorName: {
        fontSize: 16,
        fontWeight: 600,
        color: Colors.textColor
    },
    brandSelectorSlug: {
        fontSize: 14,
        color: Colors.greyDark
    },
    brandSelectorTierTag: {
        marginLeft: 'auto'
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 40,
        gap: 16
    },
    loadingText: {
        fontSize: 14,
        color: Colors.greyDark
    },
    emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 40
    },
    emptyStateText: {
        fontSize: 16,
        color: Colors.greyDark,
        textAlign: 'center'
    }
};

// Note: Hover styles can be added to App.css if needed

export default BrandSelectorModal;